import { useState } from 'react';
import { CategoryMark } from '../Classification';
import {
  ChartFrame,
  EmptyChart,
  INK,
  MotionStyles,
  moneyText,
  seriesColour,
  tickText,
  usePrefersReducedMotion,
  type ChartTable,
  type TooltipState,
} from './Chart';
import { arcPath, donutArcs, polar } from './layout';

/**
 * A ring of shares with a legend that carries the numbers.
 *
 * ── WHY A RING, WHEN `RankedBars` SAYS NOT TO ──
 * `RankedBars` is right that a comparison of magnitudes is read as lengths,
 * and it stays the answer for "what is my biggest cost". The owner asked for
 * a ring on the Overview, and the ring answers a different question: "of the
 * whole, how much is this" - a part-of-whole, which is what an angle is for.
 * The rules that keep it honest are the ones a pie usually breaks: at most
 * seven slices (the API folds the rest), a legend beside it with the name,
 * the amount AND the share on one line so the angle is never the only
 * carrier of the fact, a 2px gap of surface between slices, and the table
 * underneath with every number.
 *
 * ── THE WHOLE CIRCLE ──
 * One category is a hundred percent, and an SVG arc from a point to itself
 * paints nothing - see `arcPath`. It is drawn as two halves.
 */

export interface DonutSlice {
  key: string;
  name: string;
  emoji: string | null;
  colour: string;
  minor: number;
  share: number;
}

export interface DonutProps {
  slices: DonutSlice[];
  currency: string;
  title: string;
  summary: string;
  /** The word under the total in the middle: "out", "in". */
  centreLabel: string;
  empty: { title: string; description?: string };
}

const SIZE = 176;
const OUTER = 80;
const INNER = 54;
const CENTRE = SIZE / 2;

export function Donut({ slices, currency, title, summary, centreLabel, empty }: DonutProps) {
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = useState<number | null>(null);

  const drawn = slices.filter((slice) => slice.minor > 0);
  const arcs = donutArcs(drawn.map((slice) => slice.minor));
  if (drawn.length === 0 || arcs.length === 0) return <EmptyChart title={empty.title} description={empty.description} />;

  const total = drawn.reduce((sum, slice) => sum + slice.minor, 0);
  const percent = (share: number) => `${Math.round(share * 100)}%`;

  const tooltipFor = (i: number): TooltipState => {
    const slice = drawn[i];
    const arc = arcs[i];
    if (!slice || !arc) return { x: CENTRE, y: CENTRE, title: '', rows: [] };
    const point = polar(CENTRE, CENTRE, OUTER, (arc.start + arc.end) / 2);
    return {
      x: point.x,
      y: point.y,
      title: `${slice.emoji ? `${slice.emoji} ` : ''}${slice.name}`,
      rows: [
        { key: 'amount', label: 'Amount', value: moneyText(slice.minor, currency) },
        { key: 'share', label: 'Share', value: percent(slice.share) },
      ],
    };
  };

  const table: ChartTable = {
    caption: `${title}, in ${currency}`,
    columns: ['Category', 'Amount', 'Share'],
    rows: [
      ...drawn.map((slice) => ({
        key: slice.key,
        cells: [`${slice.emoji ? `${slice.emoji} ` : ''}${slice.name}`, moneyText(slice.minor, currency), percent(slice.share)],
      })),
      { key: 'total', cells: ['Total', moneyText(total, currency), '100%'] },
    ],
  };

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start" data-chart-donut>
      <ChartFrame title={title} table={table} tooltip={active === null ? null : tooltipFor(active)} className="w-[176px] shrink-0">
        <svg role="img" aria-label={summary} width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="block select-none">
          <defs>
            <MotionStyles enabled={!reduced} />
          </defs>
          <g style={reduced ? undefined : { animation: 'chart-fade 500ms ease-out both' }}>
            {arcs.map((arc, i) => {
              const slice = drawn[i];
              if (!slice) return null;
              return (
                <path
                  key={slice.key}
                  d={arcPath(CENTRE, CENTRE, active === i ? OUTER + 3 : OUTER, INNER, arc.start, arc.end)}
                  fill={seriesColour(slice.colour)}
                  stroke={INK.surface}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  onPointerEnter={(event) => {
                    if (event.pointerType !== 'touch') setActive(i);
                  }}
                  onPointerLeave={(event) => {
                    if (event.pointerType !== 'touch') setActive(null);
                  }}
                  onPointerDown={(event) => {
                    if (event.pointerType === 'touch') setActive((current) => (current === i ? null : i));
                  }}
                >
                  <title>{`${slice.name}: ${moneyText(slice.minor, currency)} (${percent(slice.share)})`}</title>
                </path>
              );
            })}
          </g>
          <text x={CENTRE} y={CENTRE - 2} textAnchor="middle" fontSize={15} fontWeight={600} fill={INK.text} className="tabular-nums">
            {tickText(total, currency)}
          </text>
          <text x={CENTRE} y={CENTRE + 14} textAnchor="middle" fontSize={11} fill={INK.axis}>
            {centreLabel}
          </text>
        </svg>
      </ChartFrame>

      {/* The legend: rows, not list items - the sr-only table is the
          structure a reader navigates. A long name wraps to a second line
          rather than being cut: "Consultant and pr…" beside "Software
          subscript…" was two rows a person had to guess at, and the name
          is the one thing the slice cannot say for itself. */}
      <div className="w-full min-w-0 flex-1 space-y-1.5 text-sm" data-chart-legend>
        {drawn.map((slice, i) => (
          <div
            key={slice.key}
            className={`flex items-start justify-between gap-3 rounded px-1 ${active === i ? 'bg-muted' : ''}`}
            onPointerEnter={(event) => {
              if (event.pointerType !== 'touch') setActive(i);
            }}
            onPointerLeave={(event) => {
              if (event.pointerType !== 'touch') setActive(null);
            }}
          >
            <span className="flex min-w-0 items-start gap-1.5">
              {/* Centred on the FIRST line of a name that may take two. */}
              <span className="flex h-5 shrink-0 items-center">
                <CategoryMark emoji={slice.emoji} colour={slice.colour} />
              </span>
              <span className="line-clamp-2 min-w-0 leading-5">{slice.name}</span>
            </span>

            <span className="shrink-0 leading-5 tabular-nums">
              {moneyText(slice.minor, currency)}
              <span className="ml-2 inline-block w-9 text-right text-xs text-muted-foreground">{percent(slice.share)}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
